'use client';

import { ConditionDetail } from '@components/SecondChance/ConditionDetail';
import {
  Flex,
  freddyTheme,
  Grid,
  GridItem,
  Link,
  List,
  ListItem,
  SpriteIcon,
  Typography
} from '@coolblue-development/freddy';
import readMoreTrigger from '@coolblue-development/ga4-events/triggers/SecondChanceDisclamer/readMoreTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';

export default function ProductCondition({
  productConditions,
  description,
  articleUrl,
  timeOfUsage
}: {
  productConditions: string[];
  description: string | null;
  articleUrl?: string;
  timeOfUsage?: string;
}) {
  const { t } = useTranslation('webshop.secondChance');
  const { trackEvent } = useAnalytics();

  // TODO: Modify this logic when the data is available in the correct format
  const productDescription = description
    ? description
        ?.split('\n')
        .map(s => s.trim())
        .filter(Boolean)
        .reduce<string[][]>((acc, line) => {
          if (!line.startsWith('-')) return [...acc, [line]];
          const last = acc[acc.length - 1];
          const updated = [...last, line.replace(/^\*\s*/, '')];
          return [...acc.slice(0, -1), updated];
        }, [])
    : [];

  const [damageDetails, repairedParts] = productDescription;

  const hasExtraDetails =
    (damageDetails && damageDetails.length > 1) || (repairedParts && repairedParts.length > 1);

  return (
    <Flex gap={200} direction='column'>
      <Typography variant='h3'>{t('secondChanceProductCondition')}</Typography>
      <Grid gap={400}>
        <GridItem column={{ xs: 'span 12', md: hasExtraDetails ? 'span 6' : 'span 12' }}>
          {hasExtraDetails && (
            <Typography color='subtle' component='h4' fontWeight='bold' m={0} fontSize='lg'>
              {t('generalCondition')}
            </Typography>
          )}
          <List ml={hasExtraDetails ? 300 : 0} gap='compact'>
            {timeOfUsage && <ListItem>{timeOfUsage}</ListItem>}
            {productConditions?.map(condition => (
              <ListItem key={condition}>{condition}</ListItem>
            ))}
          </List>
        </GridItem>
        {hasExtraDetails && (
          <GridItem
            column={{ xs: 'span 12', md: 'span 6' }}
            sx={{ display: 'flex', flexDirection: 'column', gap: freddyTheme.spacing[400] }}
          >
            {damageDetails && <ConditionDetail details={damageDetails} />}
            {repairedParts && <ConditionDetail details={repairedParts} />}
          </GridItem>
        )}
      </Grid>
      {articleUrl && (
        <Link
          startIcon={<SpriteIcon name='arrow-right' />}
          href={articleUrl}
          target='_blank'
          onClick={() =>
            trackEvent(
              readMoreTrigger({
                content: t('learnMore')
              })
            )
          }
        >
          {t('learnMore')}
        </Link>
      )}
    </Flex>
  );
}
